import { Modal } from '@bp/ui-components';
import { useNetworkState } from '@uidotdev/usehooks';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './OfflineModal.module.scss';

export const OfflineModal = ({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) => {
  const { t } = useTranslation();
  const { online } = useNetworkState();

  const [backendIsOnline, setBackendIsOnline] = useState<boolean>(false);

  const fetchHealth = useCallback(async () => {
    // Skip health check if browser is offline
    if (!online) {
      return;
    }

    try {
      await fetch(import.meta.env.VITE_APP_BACKEND_URI + '/_health', {
        headers: {
          'Cache-Control': 'no-cache, no-store',
          Pragma: 'no-cache',
          'If-None-Match': '', // Prevent 304 responses
          'If-Modified-Since': '',
        },
      });
      setBackendIsOnline(true);
    } catch (_error: unknown) {
      console.error(_error);
      setBackendIsOnline(false);
    }
  }, [online]);

  useEffect(() => {
    if (!open) return;

    const interval = setInterval(fetchHealth, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [open, fetchHealth]);

  useEffect(() => {
    if (online && backendIsOnline) {
      setOpen(false);
    }
  }, [online, backendIsOnline, setOpen]);

  return (
    <Modal
      parent={document.fullscreenElement ? document.getElementById('timetable-grid') : null}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
      isOpen={open}
      onRequestClose={() => setOpen(false)}
      onAfterClose={async () => {
        await fetchHealth();
      }}
      title={t('offline.connectionIssues')}
    >
      <div className={styles['offline-container']}>
        <div className={styles.message}>
          {!online && <div>{t('offline.clientOffline')}</div>}
          {!backendIsOnline && online && <div>{t('offline.serverOffline')}</div>}
          <div>{t('offline.unsavedData')}</div>
        </div>
        <div className={styles.status}>
          <div>{t('offline.autoClose')}</div>
          <div>
            <div>Server</div>
            <span className={backendIsOnline ? styles.online : styles.offline}>
              {backendIsOnline ? 'Online' : 'Offline'}
            </span>
          </div>
          <div>
            <div>Client</div>
            <span className={online ? styles.online : styles.offline}>{online ? 'Online' : 'Offline'}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
};
